import React, { useContext } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { CaretDown, CaretUp, Info } from "phosphor-react";
import { MapActions, MapContext } from "context/map";
import "./index.scss";

const LayerListMenu = ({ menuOpened, toggleMenu }) => {
  const { state, dispatch } = useContext(MapContext);
  const { selectedLayer, selectedFields, fields } = state;
  const history = useHistory();
  const layers = [
    {
      groupName: "Crop Monitor",
      items: [
        {
          name: "Standard Resolution",
          value: "standard",
          label: "SR",
        },
        {
          name: "High Resolution",
          value: "high_res",
          label: "HR",
          infoLink: "/store",
          action: () => {
            window.open("/order/high-resolution-imagery", "_blank");
          },
          actionLabel: "Order now",
        },
      ],
    },
    {
      groupName: "Soils",
      items: [
        {
          name: "Rapid-EM™",
          value: "sensor_em",
          label: "Rapid-EM™",
        },
        {
          name: "Stack",
          value: "stack",
          label: "Stack",
        },
      ],
    },
    {
      groupName: "Variable Rate",
      items: [
        {
          name: "Zones",
          value: "zoning",
          label: "Zones",
        },
      ],
    },
  ];
  const specialLayers = [
    {
      groupName: "Harvest",
      items: [
        {
          name: "Rice: Harvest Timing",
          value: "rice_harvest_prediction",
          label: "Rice: Harvest Timing",
          infoMessage:
            "Our Harvest timing tool is currently available for rice crops only.",
          beta: true,
        },
      ],
    },
  ];

  const harvestPredictionDisabled = () => {
    let disabled = false;

    if (selectedFields.length === 0) {
      disabled = true;
    }

    const filteredFields = fields.filter(
      (field) =>
        selectedFields.includes(field.id) &&
        field.properties.field_crop_type !== "Rice"
    );
    if (filteredFields.length) {
      disabled = true;
    }

    return disabled;
  };

  const getSelectedLayerLabel = () => {
    const combinedLayers = [...layers, ...specialLayers];
    return combinedLayers
      .flatMap((layer) => layer.items)
      .find((item) => item.value === selectedLayer).label;
  };

  const renderLabel = () => {
    return (
      <div
        className={`menu-button ${
          menuOpened === "layer-list" ? "-active" : ""
        }`}
        onClick={() =>
          menuOpened === "layer-list"
            ? toggleMenu(null)
            : toggleMenu("layer-list")
        }
        aria-hidden
      >
        <label className="label">Layer:</label>
        <p className="count">{getSelectedLayerLabel()}</p>
        {menuOpened === "layer-list" ? (
          <CaretUp size={16} weight="fill" color="#B6B9C2" />
        ) : (
          <CaretDown size={16} weight="fill" color="#B6B9C2" />
        )}
      </div>
    );
  };

  const generateClassName = (item) => {
    let classNames = "item";

    if (item.value === selectedLayer) {
      classNames += " selected";
    }

    if (
      harvestPredictionDisabled() &&
      item.value === "rice_harvest_prediction"
    ) {
      classNames += " disabled";
    }

    return classNames;
  };

  const toggleLayer = (item) => {
    dispatch({
      type: MapActions.SET_SELECTED_LAYER,
      value: item.value,
    });
    toggleMenu(null);
  };

  const renderLayerSelection = () => {
    return (
      <div
        className={`list-container ${
          menuOpened === "layer-list" ? "" : "-hidden"
        }`}
      >
        <p className="container-title">Layer</p>
        {layers.map((layer) => (
          <div key={layer.groupName}>
            <p>{layer.groupName}</p>
            <ul className="layer-list">
              {layer.items.map((item) => (
                <li
                  key={item.name}
                  className={generateClassName(item)}
                  onClick={() => toggleLayer(item)}
                  aria-hidden
                >
                  <p className="name">{item.name}</p>
                  {item.infoLink && (
                    <span
                      className="learn"
                      onClick={() => history.push(item.infoLink)}
                      data-tip="Learn more"
                      aria-hidden
                    >
                      <Info size={24} color="#4d515c" />
                    </span>
                  )}
                  {item.action && (
                    <div className="custom-buttons">
                      <button
                        className="redirect"
                        type="button"
                        onClick={(event) => {
                          item.action();
                          event.stopPropagation();
                        }}
                      >
                        {item.actionLabel}
                      </button>
                    </div>
                  )}
                  {item.beta && <span className="beta">New Beta</span>}
                </li>
              ))}
            </ul>
          </div>
        ))}
        {specialLayers.map((layer) => (
          <div key={layer.groupName}>
            <p>{layer.groupName}</p>
            <ul className="layer-list -special">
              {layer.items.map((item) => (
                <li
                  key={item.name}
                  className={generateClassName(item)}
                  onClick={() => {
                    if (harvestPredictionDisabled()) {
                      return;
                    }

                    toggleLayer(item);
                  }}
                  aria-hidden
                >
                  <p className="name">{item.name}</p>
                  {item.infoMessage && (
                    <span
                      className="learn"
                      data-tip={item.infoMessage}
                      aria-hidden
                    >
                      <Info size={24} color="#4d515c" />
                    </span>
                  )}
                  {item.beta && <span className="beta">New Beta</span>}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="menu-container -layer" data-tour="step-2">
      <div>
        {renderLabel()}
        {renderLayerSelection()}
      </div>
    </div>
  );
};

LayerListMenu.defaultProps = {
  menuOpened: null,
};

LayerListMenu.propTypes = {
  menuOpened: PropTypes.string,
  toggleMenu: PropTypes.func.isRequired,
};

export default LayerListMenu;
